"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useAutoRotatingIndex } from "@/hooks/useAutoRotatingIndex";

const FALLBACK_IMAGE = "/static/home/hero-illustration.webp";

type HeroCarouselProps = {
  alt?: string;
  compact?: boolean;
  fill?: boolean;
  initialImages?: string[];
};

export default function HeroCarousel({ alt, compact = false, fill = false, initialImages }: HeroCarouselProps) {
  const [images, setImages] = useState<string[]>(initialImages && initialImages.length > 0 ? initialImages : []);

  useEffect(() => {
    if (initialImages && initialImages.length > 0) return;

    fetch("/api/carousel")
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (
          data: { results?: { id: number; app_section: string; image_url: string }[] } | null
        ) => {
          const urls = (data?.results ?? [])
            .filter((item) => item.image_url)
            .map((item) => item.image_url);
          if (urls.length > 0) setImages(urls);
        }
      )
      .catch(() => {});
  }, [initialImages]);

  const currentIndex = useAutoRotatingIndex(images.length, 2000);

  const wrapperClassName = fill
    ? "absolute inset-0"
    : compact
      ? "relative z-10 flex w-full items-center md:justify-end"
      : "relative z-10 order-1 flex flex-1 items-center lg:order-2 lg:w-[620px] lg:flex-none lg:justify-end";

  const frameClassName = fill
    ? "relative h-full w-full overflow-hidden bg-brand-navy/5 dark:bg-surface-dark-raised"
    : compact
      ? "relative w-full h-[220px] sm:h-[260px] overflow-hidden rounded-card bg-brand-navy/5 shadow-card dark:bg-surface-dark-raised"
      : "relative w-full h-[280px] sm:h-[420px] lg:h-[420px] overflow-hidden rounded-2xl sm:rounded-card lg:rounded-card bg-brand-navy/5 shadow-card sm:shadow-card-lg dark:bg-surface-dark-raised";

  return (
    <div className={wrapperClassName}>
      <div className={frameClassName}>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy/10 via-transparent to-brand-red/10 z-10 pointer-events-none" />

        {images.length > 0 ? (
          <>
            {images.map((src, i) => (
              <Image
                key={src}
                src={src}
                alt={alt ?? ""}
                fill
                className={`h-full w-full object-cover transition-opacity duration-700 ${
                  i === currentIndex ? "opacity-100" : "opacity-0"
                }`}
                priority={i === 0}
                loading={i === 0 ? "eager" : "lazy"}
                quality={60}
                sizes={compact ? "(min-width: 768px) 48vw, 100vw" : "(min-width: 1024px) 620px, (min-width: 640px) 90vw, 100vw"}
              />
            ))}
            {images.length > 1 && (
              <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
                {images.map((_, i) => (
                  <span
                    key={i}
                    className={`block h-1.5 rounded-full transition-all duration-300 ${
                      i === currentIndex ? "w-5 bg-white" : "w-1.5 bg-white/50"
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <Image
            src={FALLBACK_IMAGE}
            alt={alt ?? ""}
            width={1200}
            height={900}
            className="h-full w-full object-cover"
            priority
            fetchPriority="high"
            quality={60}
            sizes={compact ? "(min-width: 768px) 48vw, 100vw" : "(min-width: 1024px) 620px, (min-width: 640px) 90vw, 100vw"}
          />
        )}
      </div>
    </div>
  );
}
